/**
 * « Signaler ou bloquer » une personne (migration 0604, App Review 1.2) —
 * ouvert depuis l'en-tête d'une discussion ou la liste des camarades.
 * Étapes : menu → signaler (ReportSheet) | confirmer le blocage → bloqué.
 */

import { useState } from 'react';
import { Ban, CheckCircle2, Flag, Undo2 } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { Sheet } from '@/features/evaluations/sheet';
import { useBlockUser, useIsBlockedByMe, useUnblockUser } from '@/lib/safety/user-safety';
import { ReportSheet } from './report-sheet';

type Step = 'menu' | 'report' | 'confirm-block' | 'blocked';

const ACTION =
  'border-ink-200 text-ink-900 flex min-h-[3.25rem] w-full items-center gap-3 rounded-xl border bg-white px-4 text-left text-sm font-semibold active:scale-[0.98] disabled:opacity-50';

export function UserSafetySheet({
  userId,
  name,
  onClose,
}: {
  userId: string;
  name: string;
  onClose: () => void;
}) {
  const [step, setStep] = useState<Step>('menu');
  const isBlocked = useIsBlockedByMe(userId);
  const block = useBlockUser();
  const unblock = useUnblockUser();
  const error = block.error ?? unblock.error;

  if (step === 'report') {
    return (
      <ReportSheet
        target={{ kind: 'user', userId }}
        targetName={name}
        alreadyBlocked={!!isBlocked}
        onClose={onClose}
      />
    );
  }

  if (step === 'blocked') {
    return (
      <Sheet
        title={`${name} est bloqué`}
        onClose={onClose}
        footer={
          <Button fullWidth onClick={onClose}>
            Fermer
          </Button>
        }
      >
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-500" aria-hidden />
          <p className="text-ink-900 font-semibold">Vous ne pouvez plus vous écrire.</p>
          <p className="text-ink-500 text-sm">
            Tu peux le débloquer à tout moment depuis ton profil, rubrique « Utilisateurs bloqués ».
          </p>
        </div>
      </Sheet>
    );
  }

  if (step === 'confirm-block') {
    return (
      <Sheet
        title={`Bloquer ${name} ?`}
        onClose={onClose}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              className="min-h-[2.75rem] w-full sm:w-auto"
              onClick={() => setStep('menu')}
            >
              Annuler
            </Button>
            <Button
              className="min-h-[2.75rem] w-full bg-red-600 hover:bg-red-700 sm:w-auto"
              disabled={block.isPending}
              onClick={() =>
                block.mutate(userId, {
                  onSuccess: () => setStep('blocked'),
                })
              }
            >
              {block.isPending ? 'Blocage…' : 'Bloquer'}
            </Button>
          </div>
        }
      >
        <ul className="text-ink-700 list-disc space-y-2 pl-5 text-sm">
          <li>Vous ne pourrez plus vous écrire.</li>
          <li>{name} ne pourra plus t’ajouter comme camarade ni se relier à toi.</li>
          <li>Ses messages ne s’afficheront plus pour toi.</li>
          <li>{name} n’est pas prévenu.</li>
        </ul>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error.message}
          </p>
        )}
      </Sheet>
    );
  }

  return (
    <Sheet title={name} onClose={onClose}>
      <div className="flex flex-col gap-2">
        <button type="button" className={ACTION} onClick={() => setStep('report')}>
          <Flag className="text-ink-500 size-5 shrink-0" aria-hidden />
          Signaler {name}
        </button>
        {isBlocked ? (
          <button
            type="button"
            className={ACTION}
            disabled={unblock.isPending}
            onClick={() => unblock.mutate(userId, { onSuccess: onClose })}
          >
            <Undo2 className="text-ink-500 size-5 shrink-0" aria-hidden />
            {unblock.isPending ? 'Déblocage…' : `Débloquer ${name}`}
          </button>
        ) : (
          <button
            type="button"
            className={`${ACTION} text-red-700`}
            disabled={isBlocked === undefined}
            onClick={() => setStep('confirm-block')}
          >
            <Ban className="size-5 shrink-0 text-red-600" aria-hidden />
            Bloquer {name}
          </button>
        )}
      </div>
      <p className="text-ink-500 text-xs">
        Un comportement qui te met mal à l’aise ? Signale-le : l’équipe JeRaisonne le traite sous 24
        heures.
      </p>
      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error.message}
        </p>
      )}
    </Sheet>
  );
}
